import { useEffect, useState, useCallback } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { ReactSortable } from 'react-sortablejs';
import FieldModal from '../FieldModal';

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconEdit = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6"/><path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
// Copy UI removed
const IconPlus = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
    style={{ width: size, height: size, stroke: 'currentColor', fill: 'none', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5"  y1="12" x2="19" y2="12" />
  </svg>
);
const IconMove = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="1" fill="currentColor"/>
    <circle cx="15" cy="6"  r="1" fill="currentColor"/>
    <circle cx="9"  cy="12" r="1" fill="currentColor"/>
    <circle cx="15" cy="12" r="1" fill="currentColor"/>
    <circle cx="9"  cy="18" r="1" fill="currentColor"/>
    <circle cx="15" cy="18" r="1" fill="currentColor"/>
  </svg>
);
const IconSpinner = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ animation: 'cf-spin 0.7s linear infinite', display: 'block' }}>
    <line x1="12" y1="2"  x2="12" y2="6"/>
    <line x1="12" y1="18" x2="12" y2="22"/>
    <line x1="4.93"  y1="4.93"  x2="7.76"  y2="7.76"/>
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
    <line x1="2"  y1="12" x2="6"  y2="12"/>
    <line x1="18" y1="12" x2="22" y2="12"/>
    <line x1="4.93"  y1="19.07" x2="7.76"  y2="16.24"/>
    <line x1="16.24" y1="7.76"  x2="19.07" y2="4.93"/>
  </svg>
);

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div style={{ position:'fixed', bottom:24, right:24, display:'flex', flexDirection:'column', gap:8, zIndex:999999, pointerEvents:'none' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          display:'flex', alignItems:'center', gap:10,
          padding:'11px 18px',
          background: t.type === 'error' ? '#fff0f1' : '#f0faf6',
          border:`1.5px solid ${t.type === 'error' ? '#f9c4c7' : '#a7e8d0'}`,
          borderRadius:10, boxShadow:'0 4px 20px rgba(20,25,60,0.12)',
          fontSize:13, fontWeight:600,
          color: t.type === 'error' ? '#c0303c' : '#1a7a55',
          minWidth:220, maxWidth:360,
          animation:'cf-toast-in 0.22s ease',
        }}>
          <span style={{ width:16, height:16, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
            {t.type === 'error'
              ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            }
          </span>
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ config, onConfirm, onCancel }) {
  if (!config) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(20,25,60,0.45)', backdropFilter:'blur(3px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999998, padding:24 }}>
      <div style={{ background:'#fff', border:'1px solid #e4e7ef', borderRadius:14, boxShadow:'0 20px 60px rgba(20,25,60,0.2)', padding:'28px 32px', maxWidth:420, width:'100%' }}>
        <div style={{ width:44, height:44, borderRadius:12, background: config.danger ? '#fff0f1' : '#eef1ff', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
          {config.danger
            ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f04c5a" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f6ef7" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          }
        </div>
        <h3 style={{ fontSize:16, fontWeight:700, margin:'0 0 8px', color:'#1a1f36' }}>{config.title}</h3>
        <p style={{ fontSize:13.5, color:'#5a6482', margin:'0 0 24px', lineHeight:1.6 }}>{config.message}</p>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button type="button" onClick={onCancel} style={{ padding:'9px 18px', borderRadius:8, fontSize:13, fontWeight:600, border:'1.5px solid #e4e7ef', background:'#fff', color:'#5a6482', cursor:'pointer' }}>Cancel</button>
          <button type="button" onClick={onConfirm} style={{ padding:'9px 18px', borderRadius:8, fontSize:13, fontWeight:600, border:'none', cursor:'pointer', background: config.danger ? '#f04c5a' : '#4f6ef7', color:'#fff', boxShadow: config.danger ? '0 2px 8px rgba(240,76,90,0.3)' : '0 2px 8px rgba(79,110,247,0.3)' }}>{config.confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RegistrationFieldsTab() {
  const [fields,        setFields]        = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [toasts,        setToasts]        = useState([]);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [deletingIds,   setDeletingIds]   = useState([]);

  // Modal state — null = closed, 'add' = add mode, {id, title} = edit mode
  const [modalState, setModalState] = useState(null);

  // ── Toast helper ───────────────────────────────────────────────────────────
  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  // ── Fetch all fields ───────────────────────────────────────────────────────
  const fetchFields = useCallback(() => {
    let mounted = true;
    setLoading(true);
    apiFetch({ path: `/wp/v2/wporg_custom_field?per_page=100&context=edit&nocache=${Date.now()}` })
      .then(res => {
        const sorted = res
          .map(item => ({ ...item, uid: item.id, order: Number(item.meta?.cfafwr_field_ajax_id ?? 9999) }))
          .sort((a, b) => a.order - b.order);
        if (mounted) setFields(sorted);
      })
      .catch(err => console.error('fetch fields error:', err))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => fetchFields(), [fetchFields]);

  // ── Sort ───────────────────────────────────────────────────────────────────
  const handleSortUpdate = (orderedFields) => {
    const fd = new FormData();
    fd.append('action', 'cfafwr_filed_sortable');
    fd.append('nonce', CFAFWR_DATA.nonce);
    orderedFields.forEach(item => fd.append('post_meta[]', item.id));
    fetch(CFAFWR_DATA.ajaxurl, { method: 'POST', body: fd }).catch(console.error);
  };

  // ── Merge newly created/duplicated fields into list ────────────────────────
  const mergeFields = (newFields) => {
    setFields(prev => {
      const maxOrder = prev.reduce((m, f) => Math.max(m, f.order), 0);
      const items = newFields.map((d, i) => ({ ...d, uid: d.id, order: maxOrder + i + 1 }));
      return [...prev, ...items];
    });
  };

  // ── Modal callbacks ────────────────────────────────────────────────────────
  const handleFieldCreated = (field) => {
    mergeFields([field]);
    addToast(`"${field.title?.rendered || 'Field'}" created successfully.`);
  };

  const handleFieldUpdated = (updated) => {
    // Update the title in the list row immediately
    setFields(prev => prev.map(f =>
      f.id === updated.id
        ? { ...f, title: { ...f.title, rendered: updated.title?.rendered || f.title?.rendered } }
        : f
    ));
    addToast(`"${updated.title?.rendered || 'Field'}" updated successfully.`);
  };

  // ── Open edit modal ────────────────────────────────────────────────────────
  const openEditModal = (fieldId, fieldTitle) => {
    setModalState({ mode: 'edit', id: fieldId, title: fieldTitle });
  };

  // Delete field via AJAX (no redirect)
  const handleDelete = useCallback(async (fieldId, fieldTitle) => {
    if (!window.confirm(`Delete "${fieldTitle}"? This cannot be undone.`)) return;
    setDeletingIds(prev => [...prev, fieldId]);
    try {
      const fd = new FormData();
      fd.append('action', 'cfafwr_delete_post');
      fd.append('post_id', fieldId);
      fd.append('nonce', CFAFWR_DATA.deleteNonce);

      const res = await fetch(CFAFWR_DATA.ajaxurl, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        setFields(prev => prev.filter(f => f.id !== fieldId));
        addToast(`\"${fieldTitle}\" deleted successfully.`);
      } else {
        addToast(data.data || 'Failed to delete field.', 'error');
      }
    } catch (err) {
      console.error('delete error:', err);
      addToast('An error occurred while deleting the field.', 'error');
    } finally {
      setDeletingIds(prev => prev.filter(id => id !== fieldId));
    }
  }, [addToast]);

  // Duplicate functionality removed

  // ── Determine if any modal is open ────────────────────────────────────────
  const isModalOpen  = modalState !== null;
  const isAddModal   = modalState === 'add';
  const isEditModal  = modalState !== null && modalState !== 'add';

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cf-card">
        <div className="cpiw-overlay-loader" style={{ position:'relative', height:200, background:'transparent', backdropFilter:'none' }}>
          <div className="cpiw-spinner" />
        </div>
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!fields.length) {
    return (
      <>
        {isModalOpen && (
          <FieldModal
            mode={isAddModal ? 'add' : 'edit'}
            editPostId={isEditModal ? modalState.id : null}
            onClose={() => setModalState(null)}
            onFieldCreated={handleFieldCreated}
            onFieldUpdated={handleFieldUpdated}
          />
        )}
        <Toast toasts={toasts} />
        <div className="cf-card">
          <div className="cf-empty-state">
            <div className="cf-empty-state__icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9"  y1="15" x2="15" y2="15" />
              </svg>
            </div>
            <h3 className="cf-empty-state__title">No Registration Fields Yet</h3>
            <p className="cf-empty-state__desc">Add your first custom field to start collecting extra data during registration.</p>
            <button type="button" className="cf-btn cf-btn--primary" onClick={() => setModalState('add')}>
              <IconPlus /> Add Registration Field
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Main list view ─────────────────────────────────────────────────────────
  return (
    <>
      {/* Copy-all overlay removed */}

      {/* ── Unified Add / Edit modal ── */}
      {isModalOpen && (
        <FieldModal
          mode={isAddModal ? 'add' : 'edit'}
          editPostId={isEditModal ? modalState.id : null}
          onClose={() => setModalState(null)}
          onFieldCreated={handleFieldCreated}
          onFieldUpdated={handleFieldUpdated}
        />
      )}

      <ConfirmDialog
        config={confirmConfig}
        onConfirm={() => pendingAction && pendingAction()}
        onCancel={() => { setConfirmConfig(null); setPendingAction(null); }}
      />

      <Toast toasts={toasts} />

      <div className="cf-card">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="cf-card__header">
          <div className="cf-card__header-left">
            <div className="cf-card__header-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </div>
            <div>
              <h2 className="cf-card__title">Registration Fields</h2>
              <p className="cf-card__description">Drag to reorder · Click edit to modify any field</p>
            </div>
          </div>

          <div className="cf-fields-header" style={{ marginBottom:0 }}>
            <span className="cf-fields-count">
              <span>{fields.length}</span>
              {fields.length === 1 ? 'field' : 'fields'}
            </span>
            {/* Duplicate-all removed */}
            <button
              type="button"
              className="cf-btn cf-btn--primary cf-btn--sm"
              onClick={() => setModalState('add')}
              style={{ marginLeft:8 }}
            >
              <IconPlus /> Add Field
            </button>
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="cf-card__body">
          <div className="cf-drag-hint">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/>
              <polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/>
              <line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/>
            </svg>
            Drag by the field name to reorder · Edit and delete without leaving this page
          </div>

          <ReactSortable
            list={fields}
            setList={setFields}
            animation={160}
            handle=".cfafwr_label"
            tag="ul"
            className="cfafwr_dl_data"
            onEnd={() => handleSortUpdate(fields)}
            ghostClass="cf-field-ghost"
            chosenClass="cf-field-chosen"
          >
            {fields.map((field) => {
              const deleteLink = `${CFAFWR_DATA.adminUrl}admin.php?page=custom-fields-registration-settings&action=delete_post&post_id=${field.id}&nonce=${CFAFWR_DATA.deleteNonce}`;
              const fieldTitle = field.title?.rendered || `Field #${field.id}`;
              const isCopy     = / \(Copy\)$/.test(fieldTitle);

              return (
                <li key={field.uid} data-id={field.uid} className="cfafwr_list_item">
                  <div className="cfafwr_add_new_fields_inner">

                    <div className="cf-field-drag-handle" aria-hidden="true">
                      <IconMove />
                    </div>

                    <span className="cfafwr_label">
                      {fieldTitle}
                      {isCopy && (
                        <span style={{ display:'inline-flex', alignItems:'center', background:'#fff8e6', color:'#b06a00', fontSize:10, padding:'2px 7px', borderRadius:20, fontWeight:600, letterSpacing:'0.3px', marginLeft:6, flexShrink:0 }}>
                          copy
                        </span>
                      )}
                    </span>

                    <span className="cfafwr_checkbox">

                      {/* ── Edit → opens modal ── */}
                      <button
                        type="button"
                        title="Edit field"
                        className="cf-field-action cf-field-action--edit"
                        onClick={e => { e.stopPropagation(); openEditModal(field.id, fieldTitle); }}
                      >
                        <IconEdit />
                      </button>

                      {/* Duplicate single removed */}

                              {/* ── Delete via AJAX ── */}
                              <button
                                type="button"
                                title="Delete field"
                                className="cf-field-action cf-field-action--delete"
                                onClick={e => { e.stopPropagation(); handleDelete(field.id, fieldTitle); }}
                                disabled={deletingIds.indexOf(field.id) !== -1}
                                style={{ opacity: deletingIds.indexOf(field.id) !== -1 ? 0.6 : 1, pointerEvents: deletingIds.indexOf(field.id) !== -1 ? 'none' : 'auto' }}
                              >
                                {deletingIds.indexOf(field.id) !== -1 ? <IconSpinner /> : <IconTrash />}
                              </button>

                    </span>
                  </div>
                </li>
              );
            })}
          </ReactSortable>

          {/* Footer */}
          <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid var(--cf-border,#e4e7ef)', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
            <span style={{ fontSize:12, color:'var(--cf-text-muted,#8c96b5)', display:'flex', alignItems:'center', gap:6 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              All changes save instantly — no page reload needed.
            </span>
            <div style={{ display:'flex', gap:8 }}>
              <button
                type="button"
                className="cf-btn cf-btn--primary cf-btn--sm"
                onClick={() => setModalState('add')}
              >
                <IconPlus /> Add Field
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
